import React, { useRef } from 'react';
import toast from 'react-hot-toast';
import { Trash2 } from 'lucide-react';
import { SectionHeading, smallInputCls, checkCls, outlineBtnCls, keywordChipCls } from './ui.jsx';
import { AVAILABLE_KEYWORDS } from './constants.js';

const checkLabelCls = 'flex items-center gap-2 text-xs font-semibold text-white/80 cursor-pointer';

// `values` holds the notification state and onChange(key, value) writes one key back.
export default function NotificationsSection({ values, onChange }) {
  const textareaRefs = useRef({});
  const { reminderTexts } = values;

  const updateReminder = (index, updates) =>
    onChange('reminderTexts', reminderTexts.map((r, i) => (i === index ? { ...r, ...updates } : r)));

  const addReminder = () =>
    onChange('reminderTexts', [
      ...reminderTexts,
      {
        id: `reminder_${Date.now()}`,
        num: 1,
        unit: 'HOURS',
        text: 'Hi #first_name, just reminding you of our upcoming appointment today at #appointment_time. See you soon! — #my_first_name',
      },
    ]);

  const removeReminder = (index) => {
    if (reminderTexts.length <= 1) {
      toast.error('At least one reminder text is required when reminder texts are enabled');
      return;
    }
    onChange('reminderTexts', reminderTexts.filter((_, i) => i !== index));
  };

  const insertKeyword = (index, keyword) => {
    const current = reminderTexts[index]?.text || '';
    const el = textareaRefs.current[index];
    if (el && el.selectionStart !== undefined) {
      const { selectionStart: start, selectionEnd: end } = el;
      updateReminder(index, { text: current.slice(0, start) + keyword + current.slice(end) });
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + keyword.length, start + keyword.length);
      }, 0);
    } else {
      updateReminder(index, { text: `${current} ${keyword}` });
    }
  };

  const keywordChips = (onPick) => (
    <div className="flex flex-wrap gap-1">
      {AVAILABLE_KEYWORDS.map((kw) => (
        <button key={kw} type="button" onClick={() => onPick(kw)} className={keywordChipCls}>
          {kw}
        </button>
      ))}
    </div>
  );

  return (
    <div className="lg:col-span-6 space-y-4">
      <SectionHeading title="General notifications" />

      <div className="space-y-1.5">
        <div className="text-xs font-semibold text-white/80">When someone books an appointment, notify me via</div>
        <div className="flex items-center gap-5 text-xs text-white/80">
          <label data-testid="text-self-for-new-appointment" className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={values.notifyViaSms} onChange={(e) => onChange('notifyViaSms', e.target.checked)} className={checkCls} />
            <span>text message</span>
          </label>
          <label data-testid="email-self-for-new-appointment" className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={values.notifyViaEmail} onChange={(e) => onChange('notifyViaEmail', e.target.checked)} className={checkCls} />
            <span>email</span>
          </label>
        </div>
      </div>

      {/* Automated action */}
      <div className="space-y-2 pt-1">
        <label data-testid="use-automated-action" className={checkLabelCls}>
          <input type="checkbox" checked={values.triggerAutomatedAction} onChange={(e) => onChange('triggerAutomatedAction', e.target.checked)} className={checkCls} />
          <span>Trigger an automated action when a new appointment is booked</span>
        </label>
        {values.triggerAutomatedAction && (
          <div className="pl-6 space-y-1">
            <label className="block text-[11px] text-white/50 font-medium">
              Select automated action <span className="text-rose-400">*</span>
            </label>
            <select value={values.automatedActionId} onChange={(e) => onChange('automatedActionId', e.target.value)} className={smallInputCls}>
              <option value="Scheduled Appointment">Scheduled Appointment</option>
              <option value="New Lead Welcome Drip">New Lead Welcome Drip</option>
              <option value="Calendar Booking Workflow">Calendar Booking Workflow</option>
              <option value="Agent Notification Only">Agent Notification Only</option>
            </select>
          </div>
        )}
      </div>

      {/* Reminder texts */}
      <div className="space-y-3 pt-1">
        <label data-testid="send-reminder-text" className={checkLabelCls}>
          <input type="checkbox" checked={values.sendReminderText} onChange={(e) => onChange('sendReminderText', e.target.checked)} className={checkCls} />
          <span>Send my leads a reminder text before the appointment</span>
        </label>

        {values.sendReminderText && (
          <div className="space-y-4 pl-6">
            {reminderTexts.map((rem, idx) => (
              <div key={rem.id || idx} className="p-3 bg-white/[0.03] border border-white/10 rounded space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-white">Reminder text {idx + 1}</span>
                  {reminderTexts.length > 1 && (
                    <button type="button" onClick={() => removeReminder(idx)} className="text-rose-400 hover:text-rose-300 p-1" title="Delete reminder text">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 text-xs text-white/80">
                  <span>Send</span>
                  <select
                    name="appointmentReminderTextNum"
                    value={rem.num}
                    onChange={(e) => updateReminder(idx, { num: Number(e.target.value) })}
                    className="border border-white/15 rounded px-2 py-1 text-xs text-white bg-[#140b22]"
                  >
                    {[5, 10, 15, 30, 45, 60, 1, 2, 3, 4, 12, 24, 48].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <select
                    name="appointmentReminderTextNumMins"
                    value={rem.unit}
                    onChange={(e) => updateReminder(idx, { unit: e.target.value })}
                    className="border border-white/15 rounded px-2 py-1 text-xs text-white bg-[#140b22]"
                  >
                    <option value="MINUTES">minutes</option>
                    <option value="HOURS">hours</option>
                    <option value="DAYS">days</option>
                  </select>
                  <span>before appointment.</span>
                </div>

                <textarea
                  ref={(el) => { textareaRefs.current[idx] = el; }}
                  rows={2}
                  maxLength={300}
                  data-testid={`appt_reminder_txt_${idx}`}
                  value={rem.text}
                  onChange={(e) => updateReminder(idx, { text: e.target.value })}
                  className={`${smallInputCls} resize-y`}
                />

                <div className="space-y-1">
                  <div className="text-[11px] font-semibold text-white/60">Available keywords:</div>
                  {keywordChips((kw) => insertKeyword(idx, kw))}
                </div>
              </div>
            ))}

            <button type="button" data-testid="add-reminder-text" onClick={addReminder} className={outlineBtnCls}>
              ADD ANOTHER REMINDER TEXT
            </button>
          </div>
        )}
      </div>

      {/* Confirmation text */}
      <div className="space-y-2 pt-1">
        <label data-testid="send-confirmation-text" className={checkLabelCls}>
          <input type="checkbox" checked={values.sendConfirmationText} onChange={(e) => onChange('sendConfirmationText', e.target.checked)} className={checkCls} />
          <span>Send my leads a confirmation text when they make an appointment</span>
        </label>
        {values.sendConfirmationText && (
          <div className="pl-6 space-y-1.5">
            <textarea rows={2} value={values.confirmationText} onChange={(e) => onChange('confirmationText', e.target.value)} className={smallInputCls} />
            {keywordChips((kw) => onChange('confirmationText', `${values.confirmationText} ${kw}`))}
          </div>
        )}
      </div>

      {/* Confirmation email */}
      <div className="space-y-2 pt-1">
        <label data-testid="send-confirmation-email" className={checkLabelCls}>
          <input type="checkbox" checked={values.sendConfirmationEmail} onChange={(e) => onChange('sendConfirmationEmail', e.target.checked)} className={checkCls} />
          <span>Send my leads a confirmation email when they make an appointment</span>
        </label>
        {values.sendConfirmationEmail && (
          <div className="pl-6 space-y-1.5">
            <textarea rows={3} value={values.confirmationEmail} onChange={(e) => onChange('confirmationEmail', e.target.value)} className={smallInputCls} />
            {keywordChips((kw) => onChange('confirmationEmail', `${values.confirmationEmail} ${kw}`))}
          </div>
        )}
      </div>
    </div>
  );
}
